import { useState, useEffect, useCallback } from 'react';
import { UGCVideoDraft } from '../types';
import {
  listVideoDrafts,
  createVideoDraft,
  deleteVideoDraft,
  VideoDraftRecord,
} from '../../../services/contentService';

export const MAX_DRAFTS = 20;

export function deriveDraftTitle(scriptContent: string): string {
  const firstLine = scriptContent.trim().split('\n')[0]?.trim();
  if (firstLine && firstLine.length > 2) {
    return firstLine.length > 40 ? firstLine.slice(0, 40) + '…' : firstLine;
  }
  return `Video Draft – ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

interface UseUGCVideoDraftsProps {
  customerId: string | null;
  idToken: string;
}

interface UseUGCVideoDraftsReturn {
  drafts: VideoDraftRecord[];
  isLoading: boolean;
  saveDraft: (state: Omit<UGCVideoDraft, 'draft_id' | 'saved_at'>) => Promise<string>;
  deleteDraft: (draftId: string) => Promise<void>;
}

export function useUGCVideoDrafts({ customerId, idToken }: UseUGCVideoDraftsProps): UseUGCVideoDraftsReturn {
  const [drafts, setDrafts] = useState<VideoDraftRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!customerId || !idToken) return;
    setIsLoading(true);
    listVideoDrafts(customerId, idToken)
      .then(setDrafts)
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [customerId, idToken]);

  const saveDraft = useCallback(async (state: Omit<UGCVideoDraft, 'draft_id' | 'saved_at'>): Promise<string> => {
    if (!customerId) return '';

    const draftId = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
      ? crypto.randomUUID()
      : `draft-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

    const now = Date.now();
    const optimistic: VideoDraftRecord = {
      draft_id: draftId,
      customer_id: customerId,
      title: state.title,
      draft_data: { ...state, draft_id: draftId, saved_at: now } as UGCVideoDraft,
      created_at: new Date(now).toISOString(),
      updated_at: new Date(now).toISOString(),
    };

    setDrafts((prev) => [optimistic, ...prev]);

    try {
      const { title, ...draftData } = state;
      await createVideoDraft(customerId, draftId, title, draftData as Omit<UGCVideoDraft, 'draft_id' | 'title' | 'saved_at'>, idToken);
      return draftId;
    } catch {
      setDrafts((prev) => prev.filter((d) => d.draft_id !== draftId));
      throw new Error('Failed to save draft');
    }
  }, [customerId, idToken]);

  const deleteDraft = useCallback(async (draftId: string): Promise<void> => {
    if (!customerId) return;

    const removed = drafts.find((d) => d.draft_id === draftId);
    setDrafts((prev) => prev.filter((d) => d.draft_id !== draftId));

    try {
      await deleteVideoDraft(customerId, draftId, idToken);
    } catch {
      if (removed) setDrafts((prev) => [...prev, removed].sort((a, b) => b.updated_at.localeCompare(a.updated_at)));
      throw new Error('Failed to delete draft');
    }
  }, [customerId, idToken, drafts]);

  return { drafts, isLoading, saveDraft, deleteDraft };
}
